import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from './components/ui/Button';
import { Layout } from './components/Layout';
import { TradeResolvedOverlay } from './components/dashboard/TradeResolvedOverlay';
import { RealtimeToasts } from './components/RealtimeToasts';
import { ThemeBackendBridge } from './components/ThemeBackendBridge';
import { WhaleLoader } from './components/WhaleLoader';
import { useRealtimeInvalidator } from './hooks/useRealtime';
import { Activity } from './pages/Activity';
import { Dashboard } from './pages/Dashboard';
import { HowItWorks } from './pages/HowItWorks';
import { Notifications } from './pages/Notifications';
import { Referrals } from './pages/Referrals';
import { Wallet } from './pages/Wallet';
import { Welcome } from './pages/Welcome';
import { CabinetLayout } from './cabinet/CabinetLayout';
import { CabActivity } from './cabinet/pages/CabActivity';
import { CabDashboard } from './cabinet/pages/CabDashboard';
import { CabNotifications } from './cabinet/pages/CabNotifications';
import { CabReferrals } from './cabinet/pages/CabReferrals';
import { CabWallet } from './cabinet/pages/CabWallet';
import { CabinetLogin } from './site/CabinetLogin';
import { LandingProduct } from './site/LandingProduct';
import { NotFound } from './site/NotFound';
import { SiteLayout } from './site/SiteLayout';
import { SiteLegal } from './site/SiteLegal';
import { BOT_URL } from './site/shared';
import { useMe } from './hooks/useApiQuery';
import { AppBaseContext } from './lib/appBase';
import { AUTH_EXPIRED_EVENT } from './lib/api';
import { authenticate, bootstrapBrowserSession } from './lib/auth';
import { isOnboarded } from './lib/onboarding';
import { startRealtime } from './lib/realtime';
import { tg } from './lib/tg';
import type { UserProfile } from './lib/types';

/**
 * Top-level surface switch.
 *
 * A real Telegram WebApp session — or the `?mock=1` dev stub, which
 * installs `window.Telegram.WebApp` — gets the in-app experience at flat
 * paths. A plain browser visitor gets the public site, the `/login`
 * page, and the authed cabinet under `/app` (the site owns `/`).
 */
export function App() {
  return inApp() ? <AppRoutes /> : <BrowserRoutes />;
}

function inApp(): boolean {
  if (tg.inTelegram) return true;
  try {
    return !!localStorage.getItem('polybot.dev.initData');
  } catch {
    return false;
  }
}

/* ── Browser surface: site + login + cabinet ───────────────────── */

function BrowserRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<CabinetLogin />} />
      <Route path="/app/*" element={<CabinetApp />} />
      <Route element={<SiteLayout />}>
        <Route path="/" element={<LandingProduct />} />
        <Route path="/legal/:doc" element={<SiteLegal />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

/* ── Browser cabinet (existing app pages, /app-prefixed) ────────── */

function CabinetApp() {
  // Every link/redirect inside the shell resolves under `/app`.
  return (
    <AppBaseContext.Provider value="/app">
      <CabinetShell />
    </AppBaseContext.Provider>
  );
}

function CabinetShell() {
  const [phase, setPhase] = useState<'init' | 'ok' | 'login'>('init');
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    void (async () => {
      // Re-mint the access token from the HttpOnly refresh cookie on
      // load. No valid cookie → not signed in → /login.
      const ok = await bootstrapBrowserSession();
      if (!ok) {
        setPhase('login');
        return;
      }
      startRealtime();
      setPhase('ok');
    })();
  }, []);

  useRealtimeInvalidator();

  useEffect(() => {
    const onExpired = () => setSessionExpired(true);
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  if (phase === 'init') return <WhaleLoader />;
  if (phase === 'login' || sessionExpired) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <ThemeBackendBridge />
      <TradeResolvedOverlay />
      <RealtimeToasts />
      <Routes>
        <Route path="how-it-works" element={<HowItWorks />} />
        <Route
          element={
            <CabinetOnboardingGate>
              <CabinetLayout />
            </CabinetOnboardingGate>
          }
        >
          <Route index element={<CabDashboard />} />
          <Route path="wallet" element={<CabWallet />} />
          <Route
            path="deposit"
            element={<Navigate to="/app/wallet?tab=deposit" replace />}
          />
          <Route
            path="withdraw"
            element={<Navigate to="/app/wallet?tab=withdraw" replace />}
          />
          <Route path="activity" element={<CabActivity />} />
          <Route path="trades" element={<Navigate to="/app/activity" replace />} />
          <Route path="notifications" element={<CabNotifications />} />
          <Route path="referrals" element={<CabReferrals />} />
        </Route>
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </>
  );
}

/**
 * Cabinet onboarding gate. Wallet creation stays in Telegram, so a
 * browser session whose account isn't onboarded yet (no `wallet_address`)
 * gets a "finish in the bot" notice rather than an empty dashboard.
 */
function CabinetOnboardingGate({ children }: { children: React.ReactNode }) {
  const me = useMe();
  if (me.isError) return <Navigate to="/login" replace />;
  if (me.isLoading || !me.data) return <WhaleLoader />;
  if (!me.data.wallet_address) return <CabinetOnboardingNotice />;
  return <>{children}</>;
}

function CabinetOnboardingNotice() {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
      <span className="pb-display text-[22px]">{t('cabinet.onboard_title')}</span>
      <span className="max-w-xs text-sm text-tg-hint">
        {t('cabinet.onboard_body')}
      </span>
      <a
        href={BOT_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="pb-btn pb-btn-primary pb-focus-ring mt-2"
      >
        {t('cabinet.open_bot')}
      </a>
    </div>
  );
}

/* ── In-app experience ─────────────────────────────────────────── */

function AppRoutes() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'init' | 'ok' | 'error'>('init');
  const [error, setError] = useState<string>('');
  const [user, setUser] = useState<UserProfile | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        // Always re-authenticate on app open. Telegram regenerates
        // ``initData`` on every WebApp launch (new ``auth_date``/
        // ``hash``/``query_id``), so a JWT cached from a previous
        // open carries an ``idh`` claim bound to the *previous*
        // initData string. The InitDataBindingMiddleware would then
        // 401 every mutation with "init_data binding mismatch", and
        // /v1/auth/refresh re-mints with the same stale ``idh`` from
        // ``users.init_data_hash``, so the user gets stuck. Calling
        // ``authenticate()`` rebinds both the JWT and the DB row to
        // the current session's initData. This also handles TG
        // account switching on the same device for free.
        const resp = await authenticate();
        setUser(resp.user);
        // Auth landed — kick off the WebSocket. Safe to call multiple
        // times; `startRealtime` is idempotent and a no-op under
        // `?mock=1`.
        startRealtime();
        setStatus('ok');
      } catch {
        // Don't surface raw error text — generic localized copy only.
        setStatus('error');
        setError(t('auth.failed'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hook the realtime stream into React Query unconditionally — even
  // before auth completes, so the first event after login lands. The
  // hook is a no-op until subscriptions actually fire.
  useRealtimeInvalidator();

  // A dead session (access token expired + silent refresh failed) is
  // broadcast by the API client. Take over the screen so the user
  // understands they're logged out, instead of seeing a generic
  // "couldn't complete that" toast on their next action.
  useEffect(() => {
    const onExpired = () => setSessionExpired(true);
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  if (sessionExpired) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
        <span className="pb-display text-[22px] text-pb-danger">
          {t('auth.session_expired_title')}
        </span>
        <span className="text-sm text-tg-hint">
          {t('auth.session_expired_body')}
        </span>
        <Button className="mt-2" onClick={() => window.location.reload()}>
          {t('auth.reload')}
        </Button>
      </div>
    );
  }

  if (status === 'init') {
    return <WhaleLoader />;
  }

  if (status === 'error') {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <span className="pb-display text-[22px] text-pb-danger">
          {t('auth.failed')}
        </span>
        <span className="text-sm text-tg-hint">{error}</span>
      </div>
    );
  }

  return (
    <>
      <ThemeBackendBridge />
      <TradeResolvedOverlay />
      <RealtimeToasts />
      <Routes>
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route
          element={
            <OnboardingGate>
              <Layout />
            </OnboardingGate>
          }
        >
          <Route path="/" element={<Dashboard welcomeUser={user} />} />
          <Route path="/wallet" element={<Wallet />} />
          <Route
            path="/deposit"
            element={<Navigate to="/wallet?tab=deposit" replace />}
          />
          <Route
            path="/withdraw"
            element={<Navigate to="/wallet?tab=withdraw" replace />}
          />
          <Route path="/activity" element={<Activity />} />
          <Route path="/trades" element={<Navigate to="/activity" replace />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/referrals" element={<Referrals />} />
        </Route>
      </Routes>
    </>
  );
}

function OnboardingGate({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  if (!isOnboarded()) {
    return <Navigate to="/welcome" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}
